import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import TTSAudioPlayer from "@/components/practice/TTSAudioPlayer";
import { Headphones, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function ListeningTab() {
  const [session, setSession] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);

  const startMutation = useMutation({
    mutationFn: () => api.post("/ai/listening/start"),
    onSuccess: ({ data }) => {
      setSession(data);
      setAnswers(new Array(data.questions.length).fill(""));
      setResult(null);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Не удалось создать упражнение"),
  });

  const submitMutation = useMutation({
    mutationFn: () => api.post(`/ai/listening/${session.id}/submit`, { answers }),
    onSuccess: ({ data }) => setResult(data),
  });

  if (!session) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 text-center" data-testid="listening-start-state">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
          <Headphones className="w-7 h-7 text-primary" />
        </div>
        <p className="font-medium">Прослушайте отрывок и ответьте на вопросы</p>
        <Button onClick={() => startMutation.mutate()} disabled={startMutation.isPending} data-testid="start-listening-button">
          {startMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Начать аудирование"}
        </Button>
      </div>
    );
  }

  if (result) {
    return (
      <div className="max-w-lg mx-auto py-6 space-y-4" data-testid="listening-result-state">
        <p className="font-heading text-xl font-bold text-center">Результат: {result.score.correct} / {result.score.total}</p>
        <div className="space-y-2">
          {result.results.map((r, i) => (
            <div
              key={i}
              className={`flex items-start gap-2 rounded-lg p-3 text-sm ${r.is_correct ? "bg-emerald-50 dark:bg-emerald-900/20" : "bg-red-50 dark:bg-red-900/20"}`}
              data-testid={`listening-result-item-${i}`}
            >
              {r.is_correct ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />}
              <div className="min-w-0">
                <p>{r.question}</p>
                {!r.is_correct && (
                  <p className="text-muted-foreground">
                    Ваш ответ: <span className="line-through">{r.user_answer || "—"}</span> · Правильно: <span className="font-medium text-foreground">{r.correct_answer}</span>
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
        <Button className="w-full" onClick={() => setSession(null)} data-testid="listening-restart-button">Новое упражнение</Button>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto py-6 space-y-5" data-testid="listening-active-state">
      <div className="bg-card border border-border rounded-2xl p-6 text-center space-y-3">
        <h3 className="font-heading text-lg font-bold">{session.topic}</h3>
        <TTSAudioPlayer text={session.passage} testIdPrefix="listening-passage-audio" />
      </div>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); submitMutation.mutate(); }}>
        {session.questions.map((q, i) => (
          <div key={i} className="space-y-1.5">
            <p className="text-sm font-medium" data-testid={`listening-question-${i}`}>{i + 1}. {q.question}</p>
            <Input
              value={answers[i] || ""}
              onChange={(e) => setAnswers((a) => a.map((v, idx) => (idx === i ? e.target.value : v)))}
              placeholder="Ваш ответ..."
              data-testid={`listening-answer-input-${i}`}
            />
          </div>
        ))}
        <Button type="submit" className="w-full" disabled={submitMutation.isPending} data-testid="listening-submit-button">
          {submitMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Проверить ответы"}
        </Button>
      </form>
    </div>
  );
}
